import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-teal-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-teal-700 mb-4">
            Privacy Policy
          </h1>
          <div className="w-24 h-1 bg-amber-500 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Welcome to Siddhant Exim Academy and Ratanlal Traders Pvt Ltd. We are committed to 
            protecting your privacy and ensuring a secure experience.
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Introduction Section */}
          <section className="p-8 border-b border-gray-200">
            <h2 className="text-2xl font-semibold text-teal-700 mb-4 flex items-center">
              <span className="w-3 h-3 bg-amber-500 rounded-full mr-3"></span>
              Introduction
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Welcome to Siddhant Exim Academy and Ratanlal Traders Pvt Ltd. We are committed to 
              protecting your privacy and ensuring a secure experience while using our services. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your 
              information when you visit our website and engage with our services.
            </p>
          </section>

          {/* Information We Collect Section */}
          <section className="p-8 border-b border-gray-200">
            <h2 className="text-2xl font-semibold text-teal-700 mb-6 flex items-center">
              <span className="w-3 h-3 bg-amber-500 rounded-full mr-3"></span>
              Information We Collect
            </h2>
            
            <div className="space-y-6">
              {/* Personal Information */}
              <div className="bg-amber-50 rounded-lg p-6 border-l-4 border-amber-500">
                <h3 className="text-xl font-semibold text-teal-700 mb-3">1. Personal Information</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Contact details (e.g., name, phone number, email address)</li>
                  <li>Professional details (e.g., company name, job title)</li>
                  <li>Any other information you provide through forms or direct communication</li>
                </ul>
              </div>

              {/* Usage Data */}
              <div className="bg-amber-50 rounded-lg p-6 border-l-4 border-amber-500">
                <h3 className="text-xl font-semibold text-teal-700 mb-3">2. Usage Data</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Details of your visits to our website, including IP address, browser type, and operating system</li>
                  <li>Information about how you use our website and services</li>
                </ul>
              </div>

              {/* Cookies */}
              <div className="bg-amber-50 rounded-lg p-6 border-l-4 border-amber-500">
                <h3 className="text-xl font-semibold text-teal-700 mb-3">3. Cookies and Tracking Technologies</h3>
                <p className="text-gray-700 ml-4">
                  We use cookies and similar technologies to enhance your experience, analyze site usage, and improve our services.
                </p>
              </div>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section className="p-8 border-b border-gray-200">
            <h2 className="text-2xl font-semibold text-teal-700 mb-6 flex items-center">
              <span className="w-3 h-3 bg-amber-500 rounded-full mr-3"></span>
              How We Use Your Information
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                "To provide and manage our services, including consulting, training, and quality control",
                "To communicate with you regarding your inquiries, requirements, and feedback",
                "To improve our website and services based on user interactions and feedback",
                "To comply with legal obligations and protect our rights"
              ].map((item, index) => (
                <div key={index} className="flex items-start">
                  <div className="flex-shrink-0 w-6 h-6 bg-teal-700 rounded-full flex items-center justify-center mr-3 mt-1">
                    <span className="text-white text-sm font-bold">{index + 1}</span>
                  </div>
                  <p className="text-gray-700">{item}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Disclosure Section */}
          <section className="p-8 border-b border-gray-200">
            <h2 className="text-2xl font-semibold text-teal-700 mb-6 flex items-center">
              <span className="w-3 h-3 bg-amber-500 rounded-full mr-3"></span>
              Disclosure of Your Information
            </h2>
            <div className="space-y-4">
              {[
                {
                  title: "Service Providers",
                  description: "Third-party companies that assist us in providing and managing our services"
                },
                {
                  title: "Business Partners",
                  description: "Companies we collaborate with for consulting, training, and other services"
                },
                {
                  title: "Legal Authorities",
                  description: "If required by law or to protect our rights"
                }
              ].map((item, index) => (
                <div key={index} className="flex items-start">
                  <div className="flex-shrink-0 w-2 h-2 bg-amber-500 rounded-full mr-3 mt-2"></div>
                  <div>
                    <h4 className="font-semibold text-teal-700">{item.title}</h4>
                    <p className="text-gray-700">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Data Security */}
          <section className="p-8 border-b border-gray-200">
            <h2 className="text-2xl font-semibold text-teal-700 mb-4 flex items-center">
              <span className="w-3 h-3 bg-amber-500 rounded-full mr-3"></span>
              Data Security
            </h2>
            <p className="text-gray-700 leading-relaxed">
              We implement appropriate technical and organizational measures to protect your personal data 
              from unauthorized access, loss, or disclosure. However, please be aware that no security 
              measures are completely infallible.
            </p>
          </section>

          {/* Your Rights */}
          <section className="p-8 border-b border-gray-200">
            <h2 className="text-2xl font-semibold text-teal-700 mb-6 flex items-center">
              <span className="w-3 h-3 bg-amber-500 rounded-full mr-3"></span>
              Your Rights
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { right: "Access", description: "Request access to the personal data we hold about you" },
                { right: "Correction", description: "Request correction of any inaccurate or incomplete data" },
                { right: "Deletion", description: "Request deletion of your personal data, subject to legal requirements" },
                { right: "Opt-Out", description: "Opt-out of receiving marketing communications from us" }
              ].map((item, index) => (
                <div key={index} className="bg-teal-50 rounded-lg p-4 border border-teal-200">
                  <h4 className="font-bold text-teal-700 text-lg mb-2">{item.right}</h4>
                  <p className="text-gray-700 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Section */}
          <section className="p-8 bg-gradient-to-r from-teal-700 to-amber-500 text-white">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <span className="w-3 h-3 bg-white rounded-full mr-3"></span>
              Contact Us
            </h2>
            <p className="mb-6 text-teal-50">
              If you have any questions or concerns about this Privacy Policy or our data practices, 
              please contact us at:
            </p>
            <div className="space-y-4">
              <div className="flex items-center">
                <div className="w-10 h-10  bg-opacity-20 rounded-full flex items-center justify-center mr-4">
                    <i className="fas fa-phone"></i>
                </div>
                <span className="text-lg font-semibold">+91 99759 22007</span>
              </div>
              <div className="flex items-center">
                <div className="w-10 h-10  bg-opacity-20 rounded-full flex items-center justify-center mr-4">
                    <i className="fas fa-envelope"></i>
                  </div>
                  <span className="text-lg font-semibold">
                    <a href="mailto:doshi.santosh@rediffmail.com" className="hover:underline">
                      doshi.santosh@rediffmail.com
                    </a>
                  </span>
              </div>
            </div>
          </section>
        </div>

        
      </div>
    </div>
  );
};

export default PrivacyPolicy;